import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetOutgoingPostbackExportQuery,
    useUpdateOutgoingPostbackMutation,
} from "api/TenantSlug/OutgoingPostback/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyPostbackOutgoingFormData } from "./formSchema";

import OutgoingPostbackData = App.Data.OutgoingPostbackData;
import UpdateOutgoingPostbackRequest = App.Http.Requests.Tracking.UpdateOutgoingPostbackRequest;

interface ApiToolGeneralFormProps {
    outgoingPostback: OutgoingPostbackData;
    onClose: () => void;
}

const ModifyPostbackOutgoingFormContent: FC<ApiToolGeneralFormProps> = ({ outgoingPostback, onClose }) => {
    const { t: tF } = usePaldockTranslation("forms.modify-postback-outgoing");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyPostbackOutgoingFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateOutgoingPostbackMutation = useUpdateOutgoingPostbackMutation(outgoingPostback.id ?? "");

    const onSubmitForm = (data: ModifyPostbackOutgoingFormData) => {
        const payload: UpdateOutgoingPostbackRequest = JSON.parse(data.outgoingPostback);

        updateOutgoingPostbackMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(tF("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getOutgoingPostback", outgoingPostback.id] });
                queryClient.refetchQueries({ queryKey: ["getOutgoingPostbackExport", outgoingPostback.id] });
                onClose();
            },
            onError: () => {
                toast.error(tF("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem
                control={control}
                name={fieldNames.outgoingPostback?.name || "outgoingPostback"}
                label={tF("fields.outgoing_postback.label")}
            >
                <Input.TextArea rows={4} placeholder={tF("fields.outgoing_postback.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingPostbackExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyPostbackOutgoingForm: FC<ApiToolGeneralFormProps> = ({ outgoingPostback, onClose }) => {
    const getOutgoingPostbackExportQuery = useGetOutgoingPostbackExportQuery(outgoingPostback?.id || "");
    const data = getOutgoingPostbackExportQuery.data;

    if (!outgoingPostback || !data) return null;
    return (
        <GeneralFormProvider<ModifyPostbackOutgoingFormData>
            schema={formSchema}
            id={FormId.TrackingToolToExportForm}
            defaultValues={{
                outgoingPostback: JSON.stringify(data.outgoingPostback),
            }}
        >
            <ModifyPostbackOutgoingFormContent outgoingPostback={outgoingPostback} onClose={onClose} />
        </GeneralFormProvider>
    );
};
